import { Button, Callout, Classes, FileInput, FormGroup, InputGroup, Intent } from '@blueprintjs/core';
import { Popover2, Classes as PopoverClasses } from '@blueprintjs/popover2';
import { styled } from '@moleculeone/ui';
import { Target } from '@retro-ui/api-contract';
import { rankItem, rankings } from '@tanstack/match-sorter-utils';
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  Row,
  RowData,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { chain } from 'lodash';
import { ChangeEvent, Dispatch, Fragment, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import { useHasAnalogsAccess } from '@f/hooks/useHasAnalogsAccess';
import { parseCsv } from '@f/utils/csv';
import { saveAsCSV } from '@f/utils/export';
import { readFileAsText } from '@f/utils/file';

import { SmilesError, TargetValidation } from '../hooks/useTargetValidation';
import { parseTargetInput } from '../utils/parseTargetInput';

import { ActionButton } from './ActionButton';
import { CompoundEditorDialog } from './CompoundEditorDialog';
import { DataInput } from './DataInput';
import { NameInput } from './NameInput';
import { SmilesInput } from './SmilesInput';
import { TargetValidationCallout } from './TargetValidationCallout';

declare module '@tanstack/table-core' {
  interface FilterFns {
    fuzzy: FilterFn<Target>;
    invalidSmiles: FilterFn<Target>;
  }
  interface TableMeta<TData extends RowData> {
    smilesErrors: SmilesError[];
  }
}

const ALLOWED_EXTENSIONS = ['.csv'];
const ROW_INNER_HEIGHT = 40;
const ROW_OUTER_HEIGHT = ROW_INNER_HEIGHT + 16;
const EMPTY_TARGET: Target = { smiles: '', name: '' };

const ActionContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  marginBottom: '1rem',
});

const SearchInput = styled(InputGroup, {
  marginLeft: 'auto',
  border: 'none',
});

const TableContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  overflow: 'auto',
});

const TableGrid = styled('div', {
  minWidth: '100%',
  display: 'grid',
  gridTemplateColumns: 'min-content 1fr 1fr min-content',
  gridAutoRows: '40px',
  gap: '1rem',
  borderCollapse: 'collapse',
});

const TableCell = styled('div', {});

const EmptyState = styled('div', {
  alignSelf: 'center',
  color: 'var(--colors-slate9)',
});

const UploadPopoverContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
});

const RowIndex = styled('div', {
  color: '$slate9',
  padding: '10px 0',
});

const Actions = styled('div', {
  display: 'flex',
});

function fuzzyFilter(row: Row<Target>, columnId: string, filterValue: string) {
  const itemRank = rankItem(row.getValue(columnId), filterValue, { threshold: rankings.CONTAINS });
  return itemRank.passed;
}

function invalidSmilesFilter(row: Row<Target>, columnId: string, filterValue: number[]) {
  const isInvalid = filterValue.includes(row.index);
  return isInvalid;
}

interface TargetInputProps {
  targets: Target[] | null;
  targetValidation: TargetValidation;
  onChange: Dispatch<SetStateAction<Target[] | null>>;
}
export function TargetInput(props: TargetInputProps) {
  const { targets, targetValidation, onChange } = props;
  const { smilesErrors } = targetValidation;

  const hasAnalogsAccess = useHasAnalogsAccess();

  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [filterInvalidSmiles, setFilterInvalidSmiles] = useState(false);
  const [recentFile, setRecentFile] = useState<File>();
  const [fileParsingError, setFileParsingError] = useState<Error>();
  const [isUploadPopoverOpen, setUploadPopoverOpen] = useState(false);
  const [drawnTargetIndex, setDrawnTargetIndex] = useState<number>();
  const [isEditorDialogOpen, setEditorDialogOpen] = useState(false);
  const [isAnalogGenerationOpen, setAnalogGenerationOpen] = useState(false);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevTargetsRef = useRef<Target[] | null>(null);
  const prevFileRef = useRef(recentFile);

  const toggleDialogOpen = (isOpen: boolean) => {
    setEditorDialogOpen(isOpen);

    if (!isOpen) {
      setAnalogGenerationOpen(false);
    }
  };

  const openUploadDialog = () => {
    setUploadPopoverOpen(true);
  };

  const closeUploadPopover = () => {
    setUploadPopoverOpen(false);
    setFileParsingError(undefined);
    setRecentFile(undefined);
  };

  const handlePaste = (rowIndex: number, value: string) => {
    const parsedValue = parseTargetInput<Target>(value);
    const newTargets = parsedValue.data.map(item => {
      return { smiles: item[0], name: item[1] };
    });

    onChange(prevTargets => {
      if (!prevTargets) {
        return newTargets;
      }

      return [...prevTargets.slice(0, rowIndex), ...newTargets, ...prevTargets.slice(rowIndex + 1)];
    });
  };

  const drawTarget = (index?: number) => {
    setDrawnTargetIndex(index);

    setEditorDialogOpen(typeof index === 'number');
  };

  const upsertTarget = (columnId: string, rowIndex: number, value: string) => {
    onChange(prevTargets => {
      if (!prevTargets) {
        return [{ ...EMPTY_TARGET, [columnId]: value }];
      }

      const newTarget = { ...EMPTY_TARGET, ...prevTargets[rowIndex], [columnId]: value };

      return [...prevTargets.slice(0, rowIndex), newTarget, ...prevTargets.slice(rowIndex + 1)];
    });
  };

  const removeTarget = (rowIndex: number) => {
    onChange(prevTargets => (prevTargets ? prevTargets.filter((_, idx) => idx !== rowIndex) : null));
  };

  const duplicateTarget = (rowIndex: number) => {
    onChange(prevTargets =>
      prevTargets
        ? [...prevTargets.slice(0, rowIndex + 1), { ...prevTargets[rowIndex] }, ...prevTargets.slice(rowIndex + 1)]
        : null
    );
  };

  const addNewEmptyTarget = (rowIndex: number) => {
    onChange(prevTargets =>
      prevTargets
        ? [...prevTargets.slice(0, rowIndex + 1), { ...EMPTY_TARGET }, ...prevTargets.slice(rowIndex + 1)]
        : null
    );
  };

  const addNewTargetsAtIndex = (rowIndex: number, targets: Target[]) => {
    onChange(prevTargets =>
      prevTargets ? [...prevTargets.slice(0, rowIndex + 1), ...targets, ...prevTargets.slice(rowIndex + 1)] : targets
    );
  };

  const resetFilters = () => {
    setGlobalFilter('');
    setFilterInvalidSmiles(false);
  };

  const updateTargets = (newTargets: Target[]) => {
    onChange(prevTargets => [...(prevTargets ?? []), ...newTargets]);
    setUploadPopoverOpen(false);
  };

  const resetTargets = () => {
    onChange(null);
    resetFilters();
  };

  const uploadFile = async (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    if (!event.currentTarget.files) {
      return;
    }

    const file = event.currentTarget.files[0];
    setRecentFile(file);

    try {
      const csvString = await readFileAsText(file);
      const targets = await parseCsv<Target[]>(csvString);

      if (targets.length === 0) {
        throw new Error('No targets found in the given file');
      }

      const headers = Object.keys(targets[0]);
      if (!headers.includes('smiles')) {
        throw new Error("Missing 'smiles' column");
      }

      setGlobalFilter('');

      onChange(prevTargets => [...(prevTargets ?? []), ...targets]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (targets.length > 0 && headers.includes('smiles')) {
        setUploadPopoverOpen(false);
      }
    } catch (error: unknown) {
      setFileParsingError(error as Error);
    }
  };

  const columns = useMemo<ColumnDef<Target>[]>(
    () => [
      {
        id: 'number',
        enableGlobalFilter: false,
        enableColumnFilter: false,
        cell: ctx => <RowIndex>{ctx.row.index + 1}</RowIndex>,
      },
      {
        id: 'smiles',
        accessorKey: 'smiles',
        cell: ctx => (
          <SmilesInput cellContext={ctx} onChange={upsertTarget} onPaste={handlePaste} onDraw={drawTarget} />
        ),
        filterFn: 'invalidSmiles',
      },
      {
        id: 'name',
        accessorKey: 'name',
        cell: ctx => <NameInput cellContext={ctx} onChange={upsertTarget} />,
      },
      {
        id: 'actions',
        enableGlobalFilter: false,
        enableColumnFilter: false,
        cell: ctx => (
          <Actions>
            {hasAnalogsAccess && (
              <ActionButton
                cellContext={ctx}
                icon="hub"
                theme="purple"
                tooltip={
                  <>
                    Generate analogs
                    <br />
                    <small>Early Preview</small>
                  </>
                }
                onActionClick={() => {
                  setAnalogGenerationOpen(true);
                  drawTarget(ctx.row.index);
                }}
              />
            )}
            <ActionButton
              data-cy="duplicate-target-button"
              cellContext={ctx}
              icon="content_copy"
              tooltip="Duplicate"
              onActionClick={duplicateTarget}
            />
            <ActionButton
              data-cy="remove-target-button"
              cellContext={ctx}
              theme="red"
              icon="delete"
              tooltip="Remove"
              onActionClick={removeTarget}
            />
          </Actions>
        ),
      },
    ],
    [targets]
  );

  const data = useMemo(() => [...(targets ?? []), { ...EMPTY_TARGET }], [targets]);

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
      invalidSmiles: invalidSmilesFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: fuzzyFilter,
    meta: { smilesErrors },
  });

  useEffect(() => {
    let filterValue;

    if (smilesErrors.length === 0) {
      setFilterInvalidSmiles(false);
    } else if (filterInvalidSmiles) {
      filterValue = chain(smilesErrors)
        .map(({ index }) => index)
        .uniq()
        .value();
    }

    table.getColumn('smiles')!.setFilterValue(filterValue);
  }, [filterInvalidSmiles, smilesErrors]);

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => ROW_OUTER_HEIGHT,
    getScrollElement: () => tableContainerRef.current,
    overscan: 10,
  });

  useEffect(() => {
    const isLastRowEmpty =
      targets &&
      targets.length > 0 &&
      targets[targets.length - 1].smiles === '' &&
      targets[targets.length - 1].name === '';
    const prevTargets = prevTargetsRef.current;

    const fileChanged = prevFileRef.current !== recentFile;

    const newTargetsInitialized = !prevTargets && targets && targets.length > 1;

    const newTargetsAppended =
      prevTargets &&
      targets &&
      prevTargets.length < targets.length &&
      prevTargets.every((target, index) => target === targets[index]);

    if (isLastRowEmpty) {
      onChange(prevTargets => prevTargets!.slice(0, -1));
    }

    if (newTargetsInitialized || newTargetsAppended || fileChanged) {
      window.setTimeout(() => {
        tableContainerRef.current?.scrollTo({
          left: 0,
          top: tableContainerRef.current?.scrollHeight ?? 0,
          behavior: 'smooth',
        });
      }, 0);
    }

    prevTargetsRef.current = targets;
    prevFileRef.current = recentFile;
  }, [targets, recentFile]);

  const { drawnTargetSmiles, drawnTargetName } = useMemo(() => {
    if (!targets || drawnTargetIndex === undefined) {
      return {
        drawnTargetSmiles: '',
        drawnTargetName: '',
      };
    }
    return {
      drawnTargetSmiles: targets[drawnTargetIndex]?.smiles ?? '',
      drawnTargetName: targets[drawnTargetIndex]?.name ?? '',
    };
  }, [targets, drawnTargetIndex]);

  const isTableEmpty = rowVirtualizer.getVirtualItems().length === 0;

  return (
    <div>
      <ActionContainer>
        <Popover2
          usePortal
          isOpen={isUploadPopoverOpen}
          onClose={closeUploadPopover}
          popoverClassName={PopoverClasses.POPOVER2_CONTENT_SIZING}
          content={
            <UploadPopoverContent data-cy="add-targets-popover-content">
              <h5 className={Classes.HEADING} data-cy="add-targets-popover-title">
                Add targets
              </h5>
              <Callout intent={Intent.PRIMARY}>
                Upload your targets using a CSV file, or input them directly in text form.
              </Callout>
              <Button
                data-cy="download-csv-template-button"
                minimal
                intent={Intent.PRIMARY}
                text="Download CSV template file"
                icon="download"
                onClick={() => {
                  saveAsCSV([], 'M1_batch_search_targets_template', { columnHeaders: ['smiles', 'name'] });
                  setUploadPopoverOpen(false);
                }}
              />
              <form>
                <FormGroup
                  label="File to upload"
                  labelInfo="(required)"
                  helperText={`Allowed extensions: ${ALLOWED_EXTENSIONS.join(', ')}`}
                >
                  <FileInput
                    style={{ width: '100%' }}
                    onInputChange={uploadFile}
                    inputProps={{
                      accept: ALLOWED_EXTENSIONS.join(','),
                    }}
                    text={recentFile?.name ?? 'No file selected'}
                  />
                </FormGroup>
                {fileParsingError && <Callout intent={Intent.DANGER}>{fileParsingError.message}</Callout>}
              </form>
              <DataInput updateTargets={updateTargets} />
            </UploadPopoverContent>
          }
        >
          <Button
            data-cy="add-targets-button"
            minimal
            active={isUploadPopoverOpen}
            intent={Intent.PRIMARY}
            text="Add targets"
            icon="import"
            onClick={openUploadDialog}
          />
        </Popover2>

        <SearchInput
          data-cy="search-targets-input"
          placeholder="Search for targets..."
          leftIcon="search"
          value={globalFilter}
          onChange={event => setGlobalFilter(event.target.value)}
          rightElement={globalFilter !== '' ? <Button large minimal icon="reset" onClick={resetFilters} /> : undefined}
          small
          round
        />
        <Button
          data-cy="clear-all-targets-button"
          minimal
          icon="trash"
          text="Clear all targets"
          onClick={resetTargets}
        />
      </ActionContainer>

      <TableContainer
        ref={tableContainerRef}
        css={{
          height: `${Math.min(((targets ? targets.length : 0) + 1) * ROW_OUTER_HEIGHT, ROW_OUTER_HEIGHT * 6)}px`,
        }}
      >
        {isTableEmpty && <EmptyState>Current filters don't match any target</EmptyState>}

        {!isTableEmpty && (
          <TableGrid
            css={{
              height: `${rowVirtualizer.getTotalSize()}px`,
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow, idx) => {
              const row = rows[virtualRow.index];

              return (
                <Fragment key={row.id}>
                  {row.getVisibleCells().map(cell => {
                    return (
                      <TableCell
                        css={{
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start - idx * virtualRow.size}px)`,
                        }}
                        key={cell.id}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </Fragment>
              );
            })}
          </TableGrid>
        )}
      </TableContainer>

      <CompoundEditorDialog
        isOpen={isEditorDialogOpen}
        setOpen={toggleDialogOpen}
        isAnalogGenerationOpen={isAnalogGenerationOpen}
        setAnalogGenerationOpen={setAnalogGenerationOpen}
        onAddAnalogsToSearch={addNewTargetsAtIndex}
        smiles={drawnTargetSmiles}
        isSmilesError={smilesErrors.some(({ index }) => index === drawnTargetIndex)}
        name={drawnTargetName}
        onSmilesChange={nextSmiles => {
          upsertTarget('smiles', drawnTargetIndex!, nextSmiles);
        }}
        onNameChange={nextName => {
          upsertTarget('name', drawnTargetIndex!, nextName);
        }}
        onTargetDuplicate={() => {
          duplicateTarget(drawnTargetIndex!);
          drawTarget(drawnTargetIndex! + 1);
        }}
        onNewEmptyTargetAdd={() => {
          addNewEmptyTarget(drawnTargetIndex!);
          drawTarget(drawnTargetIndex! + 1);
        }}
        onTargetIndexChange={(indexDiff: number) => {
          drawTarget(drawnTargetIndex! + indexDiff);
        }}
        currentTargetIndex={drawnTargetIndex!}
        targetCount={targets?.length ?? 0}
      />

      <TargetValidationCallout
        targets={targets}
        targetValidation={targetValidation}
        filterInvalidSmiles={filterInvalidSmiles}
        onFilterInvalidSmilesChange={setFilterInvalidSmiles}
      />
    </div>
  );
}
